const normalizeText = (value = '') => String(value || '').trim().toLowerCase();

const NON_EVENT_KINDS = new Set(['source_receipt', 'profile_update_receipt']);
const MEDICATION_UPDATE_CLASSIFICATIONS = new Set([
  'medication_change',
  'medication-change',
  'dose_changed',
  'schedule_changed',
  'medication_stopped',
  'medication_added',
]);
const HELPFUL_DETAIL_CLASSIFICATIONS = new Set([
  'intervention',
  'strategy',
  'support',
  'what helped',
  'what_helps',
]);

const toArray = (value) => (Array.isArray(value) ? value : []);

const getVisibleEvents = (presentationItems = []) => (
  toArray(presentationItems).flatMap((item) => {
    const itemType = normalizeText(item.type || item.timelineType);
    if (
      !item
      || NON_EVENT_KINDS.has(normalizeText(item.kind))
      || itemType === 'medication_review_summary'
      || itemType === 'medication_review_annotation'
      || item.timelineEvent === false
    ) {
      return [];
    }

    if (normalizeText(item.kind) === 'source_story') {
      return toArray(item.sourceStoryScenes).filter(Boolean);
    }

    return [item];
  })
);

const getEntryClassifications = (entry = {}) => [
  entry.category,
  entry.type,
  entry.timelineType,
  entry.logCategory,
  entry.medicationChangeType,
  entry.changeType,
].map(normalizeText).filter(Boolean);

const isMedicationUpdate = (entry = {}) => (
  getEntryClassifications(entry).some((classification) => (
    MEDICATION_UPDATE_CLASSIFICATIONS.has(classification)
  ))
);

const isHelpfulBucket = (bucket = {}) => [
  bucket.bucketType,
  bucket.knowledgeType,
  bucket.type,
  bucket.role,
  bucket.label,
].map(normalizeText).some((classification) => (
  HELPFUL_DETAIL_CLASSIFICATIONS.has(classification)
));

const getHelpfulDetailCount = (entry = {}) => {
  const structuredInterventions = [
    ...toArray(entry.interventions),
    ...toArray(entry.supportStrategies),
  ].filter(Boolean).length;
  const helpfulBuckets = toArray(entry.metadata?.storyBuckets)
    .filter(isHelpfulBucket)
    .length;

  const careDetails = toArray(entry.metadata?.careDetails || entry.careDetails)
    .filter(isHelpfulBucket).length;

  // Some entries persist the same support in both fields. Count the visible fact once.
  return Math.max(structuredInterventions, helpfulBuckets, careDetails);
};

const formatCount = (count, singular, plural = `${singular}s`) => (
  `${count} ${count === 1 ? singular : plural}`
);

export const buildTimelineDaySnapshot = (presentationItems = []) => {
  const visibleEvents = getVisibleEvents(presentationItems);
  const eventCount = visibleEvents.length;
  const medicationUpdateCount = visibleEvents.filter(isMedicationUpdate).length;
  const helpfulDetailCount = visibleEvents.reduce(
    (total, entry) => total + getHelpfulDetailCount(entry),
    0
  );
  const hasMeaningfulSummary = eventCount >= 2
    || medicationUpdateCount > 0
    || helpfulDetailCount > 0;

  if (!hasMeaningfulSummary) {
    return null;
  }

  const facts = [formatCount(eventCount, 'event')];
  if (medicationUpdateCount) {
    facts.push(formatCount(medicationUpdateCount, 'medication update'));
  }
  if (helpfulDetailCount) {
    facts.push(formatCount(helpfulDetailCount, 'thing that helped', 'things that helped'));
  }

  return {
    label: 'Today at a glance',
    text: facts.join(' · '),
    eventCount,
    medicationUpdateCount,
    helpfulDetailCount,
  };
};

export default buildTimelineDaySnapshot;
